import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import SubEntryForm from "@/components/SubEntryForm";
import PhotoLightbox from "@/components/PhotoLightbox";
import PhotoThumb from "@/components/PhotoThumb";
import TaskTimer from "@/components/TaskTimer";
import { Plus, Trash, UploadSimple, FloppyDisk, ThumbsUp, ThumbsDown, ImageSquare, PencilSimple } from "@phosphor-icons/react";
import { localDateStr } from "@/lib/date";

const kindLabels = { conversation: "Conversation", plan: "Plan", process: "Task", interview: "Interview", opportunity: "Opportunity", wisdom: "Lesson Learned", win: "Win" };
const kindClasses = { conversation: "k-conversation", plan: "k-plan", process: "k-process", interview: "k-interview", opportunity: "k-opportunity", wisdom: "k-wisdom", win: "k-win" };

// Task size units by process type (Poggs retained for legacy stored value).
const TASK_SIZE_UNIT = { Replenishment: "pages", Truck: "pieces", Planograms: "planograms", Poggs: "planograms" };

function formatDuration(mins) {
  if (mins === null || mins === undefined || mins === "") return "";
  const m = Number(mins);
  if (!Number.isFinite(m) || m <= 0) return "";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h && mm) return `${h}h ${mm}m`;
  if (h) return `${h}h`;
  return `${mm}m`;
}

function formatSize(processType, count) {
  if (count === null || count === undefined || count === "") return "";
  const unit = TASK_SIZE_UNIT[processType];
  return unit ? `${count} ${unit}` : `${count}`;
}

// Maps outcome_rating value → { icon, label } supporting new binary (0/1) and legacy 1–5 scale.
function outcomeBadge(v) {
  if (v === null || v === undefined) return null;
  // Legacy 1–5 scale: 1–2 => up, 4–5 => down, 3 => neutral (skip).
  const up = v === 1 || v === 2;
  const down = v === 0 || v === 4 || v === 5;
  if (up) return { Icon: ThumbsUp, label: "Good", cls: "text-emerald-600" };
  if (down) return { Icon: ThumbsDown, label: "Bad", cls: "text-rose-500" };
  return null;
}

export default function DailyLog() {
  const today = localDateStr();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDate = searchParams.get("date") || today;
  const focusSubId = searchParams.get("sub");
  const [date, setDate] = useState(initialDate);
  const [log, setLog] = useState(null);
  const [notes, setNotes] = useState("");
  const [showKind, setShowKind] = useState(null);
  const [editSub, setEditSub] = useState(null); // sub-entry object being edited
  const [prefillTask, setPrefillTask] = useState(null); // { duration_minutes, process_type?, notes? } from timer stop
  const [photos, setPhotos] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const saveTimer = useRef();
  const subRefs = useRef({});

  const loadPhotos = useCallback(async (logId) => {
    if (!logId) return;
    try {
      const { data } = await api.get(`/documents?log_id=${logId}`);
      setPhotos(data.filter((d) => (d.content_type || "").startsWith("image/")));
    } catch { /* transient — photos will retry on next load */ }
  }, []);

  const load = useCallback(async (d) => {
    try {
      const { data } = await api.get(`/logs/by-date/${d}`);
      setLog(data);
      setNotes(data.general_notes || "");
      loadPhotos(data.id);
    } catch {
      toast.error("Couldn't load that day — try again in a moment");
    }
  }, [loadPhotos]);

  useEffect(() => { load(date); }, [date, load]);

  // When log arrives and a focus sub-entry is requested via ?sub=, scroll to it and highlight briefly.
  useEffect(() => {
    if (!log || !focusSubId) return;
    const el = subRefs.current[focusSubId];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(focusSubId);
    const t = setTimeout(() => {
      setHighlightId(null);
      // Clear the query params so a reload doesn't keep highlighting.
      const next = new URLSearchParams(searchParams);
      next.delete("sub");
      setSearchParams(next, { replace: true });
    }, 2400);
    return () => clearTimeout(t);
  }, [log, focusSubId, searchParams, setSearchParams]);

  // Keep the URL date param in sync when the user picks a different date.
  const changeDate = (d) => {
    setDate(d);
    const next = new URLSearchParams(searchParams);
    next.set("date", d);
    next.delete("sub");
    setSearchParams(next, { replace: true });
  };

  // Autosave notes (debounced) — soft-fail on network hiccups so a transient outage doesn't crash the UI.
  useEffect(() => {
    if (!log) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put(`/logs/${log.id}`, { log_date: log.log_date, general_notes: notes })
        .catch(() => toast.error("Couldn't autosave — check your connection"));
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [notes, log]);

  const addSub = async (payload) => {
    try {
      const { data } = await api.post(`/logs/${log.id}/subentries`, payload);
      setLog({ ...log, subentries: [...(log.subentries || []), data] });
      setShowKind(null);
      setPrefillTask(null);
      toast.success("Entry added");
    } catch {
      toast.error("Couldn't save entry — try again in a moment");
    }
  };

  const updateSub = async (payload) => {
    try {
      const { data } = await api.put(`/logs/${log.id}/subentries/${editSub.id}`, payload);
      setLog({ ...log, subentries: log.subentries.map((s) => (s.id === editSub.id ? data : s)) });
      setEditSub(null);
      toast.success("Entry updated");
    } catch {
      toast.error("Couldn't update entry — try again in a moment");
    }
  };

  const removeSub = async (id) => {
    try {
      await api.delete(`/logs/${log.id}/subentries/${id}`);
      setLog({ ...log, subentries: log.subentries.filter(s => s.id !== id) });
    } catch {
      toast.error("Couldn't delete entry — try again in a moment");
    }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/upload?log_id=${log.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document attached");
      loadPhotos(log.id);
    } catch {
      toast.error("Upload failed — try again in a moment");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div data-testid="daily-log-page" className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Operations Log</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Daily entry</h1>
        </div>
        <div className="flex gap-2 items-center">
          <input data-testid="log-date-picker" type="date" value={date} onChange={e => changeDate(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2" />
          <label className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
            <UploadSimple size={16}/> Attach
            <input data-testid="upload-doc-input" type="file" className="hidden" onChange={uploadDoc}/>
          </label>
        </div>
      </header>

      <div className="bg-white rounded-2xl border border-slate-200/70 p-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">General notes</label>
          <span className="text-xs text-slate-400 inline-flex items-center gap-1"><FloppyDisk size={12}/> autosaved</span>
        </div>
        <textarea data-testid="general-notes" value={notes} onChange={e=>setNotes(e.target.value)} rows={4} placeholder="What defined the day?" className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400" />
      </div>

      <TaskTimer
        onStopped={({ minutes, label }) => {
          const processTypes = ["Replenishment","Targeted Outs","Pricing","Planograms","Truck","Backstocking","Special Event Prep"];
          const matched = processTypes.find(t => t.toLowerCase() === (label || "").trim().toLowerCase());
          const prefill = { duration_minutes: minutes };
          if (matched) prefill.process_type = matched;
          else if (label) prefill.notes = label;
          setPrefillTask(prefill);
          setEditSub(null);
          setShowKind("process");
          const detail = matched ? ` · ${matched}` : label ? ` · ${label}` : "";
          toast.success(`Logged ${minutes} min${detail} — fill in the task details`);
        }}
      />

      {photos.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-6" data-testid="log-photo-strip">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold inline-flex items-center gap-2"><ImageSquare size={22} weight="duotone" className="text-slate-500"/> Photos <span className="text-slate-400 text-sm font-normal">· {photos.length}</span></h2>
          </div>
          <p className="text-xs text-slate-400 mb-3">Tap to open · long-press to delete</p>
          <div className="flex gap-3 overflow-x-auto scrollbar-thin pb-1">
            {photos.map((p, i) => (
              <PhotoThumb
                key={p.id}
                photo={p}
                index={i}
                onClick={setLightboxIdx}
                onDeleted={(id) => setPhotos((prev) => prev.filter((x) => x.id !== id))}
              />
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200/70 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 className="text-xl font-semibold">Sub-entries</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(kindLabels).map(([k, label]) => (
              <button key={k} data-testid={`add-${k}`} onClick={()=>setShowKind(k)} className={`text-xs font-medium px-3 py-1.5 rounded-full ${kindClasses[k]} hover:opacity-90`}>
                <Plus size={12} className="inline mr-1"/> {label}
              </button>
            ))}
          </div>
        </div>

        {showKind && !editSub && (
          <SubEntryForm
            kind={showKind}
            initial={showKind === "process" && prefillTask ? prefillTask : null}
            onCancel={() => { setShowKind(null); setPrefillTask(null); }}
            onSave={addSub}
          />
        )}

        <div className="mt-4 space-y-3">
          {(!log?.subentries || log.subentries.length === 0) && !showKind && (
            <div className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-xl">No entries yet. Choose a category above.</div>
          )}
          {log?.subentries?.map(s => (
            editSub?.id === s.id ? (
              <div
                key={s.id}
                ref={(el) => { if (el) subRefs.current[s.id] = el; else delete subRefs.current[s.id]; }}
                data-testid={`subentry-${s.id}-editing`}
              >
                <SubEntryForm kind={s.kind} initial={s} onCancel={() => setEditSub(null)} onSave={updateSub} />
              </div>
            ) : (
            <div
              key={s.id}
              ref={(el) => { if (el) subRefs.current[s.id] = el; else delete subRefs.current[s.id]; }}
              data-testid={`subentry-${s.id}`}
              className={`border rounded-xl p-4 flex justify-between items-start gap-4 transition-all duration-500 ${highlightId === s.id ? "border-teal-400 shadow-[0_0_0_3px_rgba(20,184,166,0.18)] bg-teal-50/40" : "border-slate-200/70"}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${kindClasses[s.kind]}`}>{kindLabels[s.kind]}</span>
                  <span className="text-sm font-medium">{s.subject || s.process_type || s.position || ""}</span>
                </div>
                {s.participants?.length > 0 && <div className="text-xs text-slate-500">{s.kind === "win" ? "People" : "With"}: {s.participants.join(", ")}</div>}
                {s.leader && <div className="text-xs text-slate-500">Leader: {s.leader}{s.team?.length ? ` · Team: ${s.team.join(", ")}` : ""}</div>}
                {s.applicant_name && <div className="text-xs text-slate-500">Applicant: {s.applicant_name} · Rating: {s.rating}/10</div>}
                {(() => {
                  const b = outcomeBadge(s.outcome_rating);
                  if (!b) return null;
                  const { Icon, label, cls } = b;
                  return (
                    <div className={`text-xs mt-1 inline-flex items-center gap-1 ${cls}`}>
                      Outcome: <Icon weight="fill" size={14}/> {label}
                    </div>
                  );
                })()}
                {s.notes && <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{s.notes}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`edit-sub-${s.id}`} onClick={() => { setEditSub(s); setShowKind(null); }} className="text-slate-400 hover:text-teal-600 p-1" title="Edit"><PencilSimple size={16}/></button>
                <button data-testid={`del-sub-${s.id}`} onClick={()=>removeSub(s.id)} className="text-slate-400 hover:text-red-500 p-1" title="Delete"><Trash size={16}/></button>
              </div>
            </div>
            )
          ))}
        </div>
      </div>

      {lightboxIdx !== null && (
        <PhotoLightbox photos={photos} startIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} />
      )}
    </div>
  );
}
